import type { N8nCredentialSummary } from '@n8n-builder/api-client';
import type { CredentialRequirement } from '../../store/credentials';
import { useCredentialsStore } from '../../store/credentials';
import { CheckIcon, ExternalLinkIcon, ChevronDownIcon } from '../ui/icons';
import { CredentialForm } from './CredentialForm';

interface CredentialRequirementCardProps {
  requirement: CredentialRequirement;
  matchingCredentials: N8nCredentialSummary[];
  instanceUrl: string;
  applied: boolean;
  onRefresh: () => void;
  onRemove: () => void;
}

function humanizeType(type: string): string {
  return type
    .replace(/Api$/, ' API')
    .replace(/OAuth2$/, ' OAuth2')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase());
}

export function CredentialRequirementCard({
  requirement,
  matchingCredentials,
  instanceUrl,
  applied,
  onRefresh,
  onRemove,
}: CredentialRequirementCardProps) {
  const { mapCredential, unmapCredential, activeFormType, setActiveFormType, addAvailableCredential } = useCredentialsStore();
  const { credentialType, nodeNames, isOAuth2, mappedCredential } = requirement;
  const isMapped = !!mappedCredential;
  const showForm = activeFormType === credentialType;

  const handleSelectCredential = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = e.target.value;
    if (!selectedId) {
      unmapCredential(credentialType);
      return;
    }
    const cred = matchingCredentials.find((c) => c.id === selectedId);
    if (cred) {
      mapCredential(credentialType, { id: cred.id, name: cred.name });
    }
  };

  const handleCreated = (credential: { id: string; name: string }) => {
    addAvailableCredential({ id: credential.id, name: credential.name, type: credentialType, createdAt: '', updatedAt: '' });
    mapCredential(credentialType, credential);
    setActiveFormType(null);
  };

  return (
    <div className={`rounded-lg border ${isMapped ? 'border-green-200 bg-green-50/30' : 'border-gray-200 bg-white'}`}>
      <div className="p-3">
        {/* Header — credential type, node badges, and actions inline */}
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-sm font-medium text-gray-900 shrink-0">
            {humanizeType(credentialType)}
          </span>
          <div className="flex flex-wrap gap-1 min-w-0">
            {nodeNames.map((name) => (
              <span
                key={name}
                className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600"
              >
                {name}
              </span>
            ))}
          </div>
          <div className="flex items-center gap-1.5 ml-auto shrink-0">
            {isMapped && (
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-green-100">
                <CheckIcon className="h-3 w-3 text-green-600" />
              </span>
            )}
            {isMapped && applied && (
              <button
                onClick={onRemove}
                className="text-xs text-gray-400 hover:text-red-500"
              >
                Remove
              </button>
            )}
            {isMapped && !applied && (
              <button
                onClick={() => unmapCredential(credentialType)}
                className="text-xs text-gray-400 hover:text-gray-600"
              >
                Change
              </button>
            )}
          </div>
        </div>

        {/* OAuth2 info banner */}
        {isOAuth2 && !isMapped && (
          <div className="mb-2 rounded-md bg-blue-50 p-2">
            <p className="text-xs text-blue-700">
              OAuth2 credentials must be set up in n8n's UI.{' '}
              <a
                href={`${instanceUrl}/home/credentials`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-0.5 font-medium underline"
              >
                Open n8n credentials
                <ExternalLinkIcon className="h-3 w-3" />
              </a>
            </p>
          </div>
        )}

        {/* Mapped state */}
        {isMapped ? (
          <div className="rounded-md bg-gray-50 px-2.5 py-1.5">
            <p className="text-sm text-gray-700">{mappedCredential.name}</p>
          </div>
        ) : (
          <>
            {/* Dropdown for existing credentials */}
            {matchingCredentials.length > 0 ? (
              <div className="relative">
                <select
                  value=""
                  onChange={handleSelectCredential}
                  className="w-full appearance-none rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 pr-8 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value="">Select credential...</option>
                  {matchingCredentials.map((cred) => (
                    <option key={cred.id} value={cred.id}>
                      {cred.name}
                    </option>
                  ))}
                </select>
                <ChevronDownIcon className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              </div>
            ) : (
              <p className="text-xs text-gray-400 italic mb-1">
                No existing {humanizeType(credentialType)} credentials found
              </p>
            )}

            {/* Create / Refresh buttons */}
            <div className="flex gap-2 mt-2">
              {!isOAuth2 && !showForm && (
                <button
                  onClick={() => setActiveFormType(credentialType)}
                  className="border border-dashed border-gray-300 rounded-md px-2.5 py-1 text-xs font-medium text-gray-600 hover:border-gray-400 hover:text-gray-700"
                >
                  + Create New
                </button>
              )}
              {isOAuth2 && (
                <button
                  onClick={onRefresh}
                  className="border border-dashed border-gray-300 rounded-md px-2.5 py-1 text-xs font-medium text-blue-600 hover:border-blue-400 hover:text-blue-700"
                >
                  Refresh credentials
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {/* Inline create form */}
      {showForm && (
        <CredentialForm
          credentialType={credentialType}
          onCreated={handleCreated}
          onCancel={() => setActiveFormType(null)}
        />
      )}
    </div>
  );
}
